import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import VideoPreviousOverlayIcon from '@atlaskit/icon/core/video-previous-overlay';

import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';

import { AudioControlIconButton } from './sub-components/AudioControlButton';

const START_SECONDS = 0;
const ZERO_EPSILON_SECONDS = 0.25;

const i18n = defineMessages({
	playFromBeginningLabel: {
		id: 'audio.miniplayer-controls.play-from-beginning-label',
		defaultMessage: 'Play from beginning',
		description:
			'The label is used for accessibility for the icon which will be shown in the menu item that will restart the active audio from the beginning',
	},
});

export type PlayFromBeginningButtonProps = {
	isDisabled?: boolean;
	onClick?: () => void;
};

export const PlayFromBeginningButton = ({
	isDisabled = false,
	onClick = () => {},
}: PlayFromBeginningButtonProps): React.ReactNode => {
	const intl = useIntl();

	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();

	const hasBufferedAtZero = (audio: HTMLAudioElement) => {
		try {
			const ranges = audio.buffered;
			for (let i = 0; i < ranges.length; i++) {
				const start = ranges.start(i);
				const end = ranges.end(i);
				if (start <= ZERO_EPSILON_SECONDS && end > ZERO_EPSILON_SECONDS) {
					return true;
				}
			}
		} catch {}
		return false;
	};

	const handlePlayFromBeginning = async () => {
		const audio = audioRef.current;
		if (audio) {
			if (hasBufferedAtZero(audio)) {
				audio.currentTime = START_SECONDS;
				if (audio.paused) {
					await audio.play();
				}
			} else {
				fetchAudio();
			}
		}
	};

	const handleClick = () => {
		onClick?.();
		handlePlayFromBeginning();
	};

	return (
		<AudioControlIconButton
			Icon={VideoPreviousOverlayIcon}
			label={intl.formatMessage(i18n.playFromBeginningLabel)}
			tooltip={intl.formatMessage(i18n.playFromBeginningLabel)}
			onClick={handleClick}
			isDisabled={isDisabled}
			testId="miniplayer-play-from-beginning"
		/>
	);
};
